import { Injectable, computed, signal } from '@angular/core';
import { api, getApiToken, setApiToken } from './api';
import type { LoopOrg, LoopOutlet, LoopUser } from './types';

const SESSION_KEY = 'rs_loop_session_v1';
const OUTLET_KEY = 'rs_loop_outlet_v1';

export interface LoopSession {
  user: LoopUser;
  org: LoopOrg;
  accessToken: string;
  authKind: string;
  signedInAt: string;
  mustChangePassword: boolean;
}

interface MeContext {
  user?: LoopUser & { mustChangePassword?: boolean };
  org?: LoopOrg;
  outlets?: LoopOutlet[];
  authKind?: string;
  actingAs?: Record<string, unknown>;
  entitlements?: { productId?: string; product?: string; status?: string }[];
  memberships?: { productId: string; outletId: string; role?: string; status?: string }[];
}

@Injectable({ providedIn: 'root' })
export class LoopSessionService {
  private readonly _session = signal<LoopSession | null>(this.read());
  private readonly _context = signal<MeContext | null>(null);
  private readonly _outletId = signal<string | null>(
    typeof localStorage !== 'undefined' ? localStorage.getItem(OUTLET_KEY) : null,
  );

  readonly session = this._session.asReadonly();
  readonly context = this._context.asReadonly();
  readonly outletId = this._outletId.asReadonly();
  readonly isSignedIn = computed(() => !!this._session()?.accessToken);
  readonly mustChangePassword = computed(
    () =>
      this._session()?.mustChangePassword === true ||
      this._context()?.user?.mustChangePassword === true,
  );
  readonly isActing = computed(
    () =>
      this._session()?.authKind === 'team_acting' ||
      this._context()?.authKind === 'team_acting' ||
      this._context()?.authKind === 'impersonation',
  );
  readonly orgId = computed(() => this._session()?.org?.id || this._context()?.org?.id || '');
  readonly orgName = computed(
    () => this._session()?.org?.name || this._context()?.org?.name || 'Organization',
  );

  constructor() {
    const s = this._session();
    if (s?.accessToken) setApiToken(s.accessToken);
  }

  hasLoopAccess(): boolean {
    const ents = this._context()?.entitlements || [];
    if (
      ents.some((e) => {
        const id = e.productId || e.product;
        return (id === 'loop' || id === 'circle') && (e.status === 'ACTIVE' || e.status === 'TRIAL');
      })
    ) {
      return true;
    }
    const mems = this._context()?.memberships || [];
    return mems.some(
      (m) =>
        (m.productId === 'loop' || m.productId === 'circle') &&
        (!m.status || m.status === 'active'),
    );
  }

  async signIn(email: string, password: string): Promise<void> {
    const data = await api<{
      accessToken: string;
      user: LoopUser & { mustChangePassword?: boolean };
      org?: LoopOrg;
      authKind?: string;
      mustChangePassword?: boolean;
    }>('/v1/auth/app/password-login', {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), password }),
    });
    const session: LoopSession = {
      user: data.user,
      org: data.org || { id: '', name: 'Organization' },
      accessToken: data.accessToken,
      authKind: data.authKind || 'firebase',
      signedInAt: new Date().toISOString(),
      mustChangePassword:
        data.mustChangePassword === true || data.user?.mustChangePassword === true,
    };
    setApiToken(session.accessToken);
    this._session.set(session);
    this.persist(session);
    await this.hydrate();
  }

  async signInActing(token: string): Promise<void> {
    const trimmed = token.trim();
    if (!trimmed.startsWith('rsact_') && !trimmed.startsWith('rsimp_')) {
      throw new Error('Invalid acting token');
    }
    setApiToken(trimmed);
    const session: LoopSession = {
      user: { id: '', name: 'Acting' },
      org: { id: '', name: 'Organization' },
      accessToken: trimmed,
      authKind: trimmed.startsWith('rsact_') ? 'team_acting' : 'impersonation',
      signedInAt: new Date().toISOString(),
      mustChangePassword: false,
    };
    this._session.set(session);
    this.persist(session);
    await this.hydrate();
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const data = await api<{
      mustChangePassword: boolean;
      accessToken?: string | null;
    }>('/v1/auth/app/password-change', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (data.accessToken) {
      setApiToken(data.accessToken);
    }
    const cur = this._session();
    if (cur) {
      const next = {
        ...cur,
        accessToken: data.accessToken || cur.accessToken,
        mustChangePassword: false,
        user: { ...cur.user, mustChangePassword: false },
      };
      this._session.set(next);
      this.persist(next);
    }
    await this.hydrate();
  }

  async hydrate(): Promise<void> {
    if (!getApiToken()) return;
    const ctx = await api<MeContext>('/v1/me/context');
    this._context.set(ctx);
    if (this._session()) {
      const next = {
        ...this._session()!,
        org: ctx.org
          ? { id: ctx.org.id, name: ctx.org.name }
          : this._session()!.org,
        user: ctx.user || this._session()!.user,
        authKind: ctx.authKind || this._session()!.authKind,
        mustChangePassword: false as boolean,
      };
      // Acting sessions never force password change; otherwise trust /me/context.
      if (next.authKind === 'team_acting' || next.authKind === 'impersonation') {
        next.mustChangePassword = false;
      } else {
        next.mustChangePassword = ctx.user?.mustChangePassword === true;
      }
      this._session.set(next);
      this.persist(next);
    }
    const outlets = ctx.outlets || [];
    let oid = this._outletId();
    if (!oid || !outlets.some((o) => o.id === oid)) {
      oid = outlets[0]?.id || null;
      this.selectOutlet(oid);
    }
  }

  selectOutlet(id: string | null): void {
    this._outletId.set(id);
    if (typeof localStorage !== 'undefined') {
      if (id) localStorage.setItem(OUTLET_KEY, id);
      else localStorage.removeItem(OUTLET_KEY);
    }
  }

  outlets(): LoopOutlet[] {
    return this._context()?.outlets || [];
  }

  signOut(): void {
    this._session.set(null);
    this._context.set(null);
    setApiToken(null);
    if (typeof localStorage !== 'undefined') localStorage.removeItem(SESSION_KEY);
  }

  private persist(s: LoopSession): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  }

  private read(): LoopSession | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as LoopSession;
      return {
        ...parsed,
        mustChangePassword: parsed.mustChangePassword === true,
      };
    } catch {
      return null;
    }
  }
}
